import React from 'react';

import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';

import { expValEquals } from '@atlassian/jira-feature-experiments';
import Placeholder from '@atlassian/jira-placeholder';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { lazyAfterPaint } from '@atlassian/react-loosely-lazy';
import { PACKAGE_NAME, TEAM_NAME } from '../../common/constants.tsx';
import {
	useIsEligibleForTrialAssistantWelcome,
	useJiraEntitlementId,
} from '../../services/eligibility-check/index.tsx';
import { useOpenTrialAssistantAgent } from '../../services/rovo-trigger/index.tsx';
import { ManualTrialCondition } from '../common/ManualTrialCondition.tsx';
import type UpgradeModalWrapperType from './index.tsx';

const AsyncWelcomeModal = lazyAfterPaint<typeof UpgradeModalWrapperType>(() =>
	import(/* webpackChunkName: "async-jira-trial-assistant-upgrade-modal" */ './index.tsx').then(
		(module) => module.WelcomeModalWrapper,
	),
);

const JiraTrialAssistantWelcomeAsyncWrapper = () => {
	const triggerRovo = useOpenTrialAssistantAgent('jira-trial-reminder-spotlight');

	if (!expValEquals('custom_rovo_onboarding_agent', 'cohort', 'variation', 'not-enrolled')) {
		return <></>;
	}

	return (
		<JSErrorBoundary
			id="JiraTrialAssistantWelcomeModal"
			packageName={PACKAGE_NAME}
			teamName={TEAM_NAME}
			fallback="unmount"
		>
			<UFOSegment name="jira-trial-assistant-welcome-modal">
				<Placeholder name="jira-trial-assistant-welcome-modal" fallback={null}>
					<AsyncWelcomeModal onPrimaryCta={triggerRovo} />
				</Placeholder>
			</UFOSegment>
		</JSErrorBoundary>
	);
};

const JiraTrialAssistantWelcomeModal = () => {
	const jiraEntitlementId = useJiraEntitlementId();
	const isEligible = useIsEligibleForTrialAssistantWelcome() && !!jiraEntitlementId;

	if (!isEligible) {
		return <></>;
	}

	return (
		<ManualTrialCondition entitlementId={jiraEntitlementId}>
			<JiraTrialAssistantWelcomeAsyncWrapper />
		</ManualTrialCondition>
	);
};

// Note: this component is renamed at export to avoid the term "Modal",
// which often correlates to an entry point. This UI opens automatically and not via trigger.
export const JiraTrialAssistantWelcomeAutoPopup = JiraTrialAssistantWelcomeModal;
